import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { Search, Settings, Save, X, Play, ToggleLeft, ToggleRight, Key, CheckCircle2, AlertCircle, Trash2, Bot, FlaskConical, ShieldCheck } from 'lucide-react'
import { formatCurrency } from '@roi365/shared'

interface AiTenantRow {
  id: string
  name: string
  plan: string
  ai_enabled: boolean
  strategy: string
  visibility_mode: string
  auto_publish: boolean
  generation_frequency: string
  last_run_at: string | null
  recommendation_count: number
  agent_configured: number | null
  agent_check_status: string | null
  active_runs: number
}

interface AiTenantConfig {
  feature_enabled: boolean
  visibility_mode: string
  auto_publish: boolean
  strategy: string
  generation_frequency: string
  max_investigations_per_run: number
  max_minutes_per_investigation: number
  max_tokens_per_run: number
  monthly_token_budget: number
  max_tool_calls_per_investigation: number
}

const strategyLabels: Record<string, string> = {
  automatic: 'Automatica',
  conservative: 'Conservadora',
  aggressive: 'Agresiva',
  high_confidence_only: 'Solo alta confianza',
}

const visibilityModeLabels: Record<string, string> = {
  all: 'Todas',
  approved_only: 'Solo aprobadas',
  none: 'Ninguna',
}

interface OpenAiKeyInfo {
  key_hint: string
  model: string
  is_active: number
  last_used_at: string | null
  last_check_at: string | null
  last_check_status: string | null
  last_check_error: string | null
  agent_id: string | null
  agent_name: string | null
  agent_model: string | null
  agent_tools_json: string | null
  agent_check_at: string | null
  agent_check_status: string | null
  agent_check_error: string | null
  price_input_per_m: number | null
  price_output_per_m: number | null
  effective: { context_version: string; prompt_version: string; session_tools: string[]; environment: string; output: string }
}

interface Budget {
  limits: { max_investigations_per_run: number; max_minutes_per_investigation: number; max_tokens_per_run: number; monthly_token_budget: number; max_tool_calls_per_investigation: number }
  month: { tokens: number; tasks: number; skipped_budget: number; cost_usd: number | null; cost_eur: number | null; chat_threads?: number; chat_general_threads?: number; chat_tokens?: number; chat_cost_eur?: number | null } | null
  activeRun: boolean
}

interface UsageData {
  byDay: Array<{ date: string; tokens_input: number; tokens_output: number; cost_usd: number; cost_eur: number; runs: number }>
  totals: { total_tokens_input: number; total_tokens_output: number; total_cost_usd: number; total_cost_eur: number; total_runs: number; failed_runs: number; runs_without_cost: number }
  days: number
}

const DEFAULT_MODEL = 'gpt-5.4-mini-2026-03-17'
const inputClass = 'w-full rounded-md border px-3 py-2 text-sm'
const inputStyle = { borderColor: 'var(--color-border)' }

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{label}</span>
      <span className="text-xs text-right tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{children}</span>
    </div>
  )
}

function ResearchPanel({ tenantId, tenantName, onClose }: { tenantId: string; tenantName: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(DEFAULT_MODEL)
  const [agentId, setAgentId] = useState('')
  const [priceIn, setPriceIn] = useState('')
  const [priceOut, setPriceOut] = useState('')
  const [usageDays, setUsageDays] = useState(30)

  const { data: keyInfo, isLoading: keyLoading } = useQuery({
    queryKey: ['admin', 'ai', 'openai-key', tenantId],
    queryFn: () => api.get<OpenAiKeyInfo | null>(`/admin/ai/tenants/${tenantId}/openai-key`),
  })
  const { data: budget } = useQuery({
    queryKey: ['admin', 'ai', 'budget', tenantId],
    queryFn: () => api.get<Budget>(`/admin/ai/tenants/${tenantId}/budget`),
    refetchInterval: 15_000,
  })
  const { data: usage } = useQuery({
    queryKey: ['admin', 'ai', 'usage', tenantId, usageDays],
    queryFn: () => api.get<UsageData>(`/admin/ai/tenants/${tenantId}/usage?days=${usageDays}`),
  })

  useEffect(() => {
    if (!keyInfo) return
    setModel(keyInfo.model)
    setAgentId(keyInfo.agent_id ?? '')
    setPriceIn(keyInfo.price_input_per_m === null ? '' : String(keyInfo.price_input_per_m))
    setPriceOut(keyInfo.price_output_per_m === null ? '' : String(keyInfo.price_output_per_m))
  }, [keyInfo])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'openai-key', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'usage', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'budget', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'tenants'] })
  }
  const onErr = (err: unknown) => toast.error((err as Error).message)

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.put(`/admin/ai/tenants/${tenantId}/openai-key`, payload),
    onSuccess: () => { toast.success('Configuración guardada'); setApiKey(''); invalidate() },
    onError: onErr,
  })
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/admin/ai/tenants/${tenantId}/openai-key`),
    onSuccess: () => { toast.success('API key eliminada'); invalidate() },
    onError: onErr,
  })
  const testKeyMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string; error?: string }>(`/admin/ai/tenants/${tenantId}/openai-key/test`),
    onSuccess: (data) => { if (data.ok) toast.success('Clave verificada'); else toast.error(`Fallo: ${data.status} ${data.error ?? ''}`); invalidate() },
    onError: onErr,
  })
  const validateAgentMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status: string; error: string | null; warnings: string[] }>(`/admin/ai/tenants/${tenantId}/agent/validate`),
    onSuccess: (data) => {
      if (data.ok) toast.success('Agente accesible con la clave de este tenant')
      else toast.error(`Agente no accesible: ${data.status}${data.error ? ' — ' + data.error : ''}`)
      for (const w of data.warnings) toast.info(w)
      invalidate()
    },
    onError: onErr,
  })
  const testResearchMutation = useMutation({
    mutationFn: () => api.post<{ runId: string }>(`/admin/ai/tenants/${tenantId}/research-test`),
    onSuccess: () => { toast.success('Prueba sintética encolada. Consulta Ejecuciones IA.'); invalidate() },
    onError: onErr,
  })

  const handleSave = () => {
    const payload: Record<string, unknown> = {}
    if (apiKey) payload.apiKey = apiKey.trim()
    if (model && model !== keyInfo?.model) payload.model = model
    if ((agentId || null) !== (keyInfo?.agent_id ?? null)) payload.agentId = agentId ? agentId.trim() : null
    const pin = priceIn === '' ? null : Number(priceIn)
    const pout = priceOut === '' ? null : Number(priceOut)
    if (pin !== (keyInfo?.price_input_per_m ?? null)) payload.priceInputPerM = pin
    if (pout !== (keyInfo?.price_output_per_m ?? null)) payload.priceOutputPerM = pout
    if (Object.keys(payload).length === 0) { toast.info('Sin cambios para guardar'); return }
    saveMutation.mutate(payload)
  }

  const agentTools: Array<{ type: string; mode?: string | null; name?: string }> = keyInfo?.agent_tools_json ? (JSON.parse(keyInfo.agent_tools_json) as Array<{ type: string; mode?: string | null; name?: string }>) : []
  const monthTokens = budget?.month?.tokens ?? 0
  const monthPct = budget ? Math.min(100, Math.round((monthTokens / Math.max(1, budget.limits.monthly_token_budget)) * 100)) : 0

  return (
    <div className="border-t p-6" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Investigador IA — {tenantName}</h4>
        <Button variant="secondary" size="sm" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Credentials + agent */}
        <div className="space-y-3 rounded-md border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Clave y agente guardado</p>
          {keyLoading ? (
            <div className="h-20 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
          ) : keyInfo ? (
            <div className="space-y-1.5">
              <Row label="Clave">
                <code className="rounded px-2 py-0.5" style={{ backgroundColor: 'var(--color-gray-100)' }}>{keyInfo.key_hint}</code>
              </Row>
              <Row label="Estado de la clave">
                {keyInfo.last_check_status === 'ok' ? <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-success-dark)' }}><CheckCircle2 className="h-3.5 w-3.5" /> Verificada {keyInfo.last_check_at && `(${formatDateTime(keyInfo.last_check_at)})`}</span>
                  : keyInfo.last_check_status ? <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-error)' }} title={keyInfo.last_check_error ?? ''}><AlertCircle className="h-3.5 w-3.5" /> {keyInfo.last_check_status}</span>
                  : 'Sin verificar'}
              </Row>
              {keyInfo.last_check_status && keyInfo.last_check_status !== 'ok' && keyInfo.last_check_error && <p className="text-xs" style={{ color: 'var(--color-error)' }}>{keyInfo.last_check_error}</p>}
              <Row label="Agente">{keyInfo.agent_id ? <code>{keyInfo.agent_id}</code> : <span style={{ color: 'var(--color-warning-dark)' }}>Sin vincular</span>}</Row>
              <Row label="Estado del agente">
                {keyInfo.agent_check_status === 'ok' ? <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-success-dark)' }}><CheckCircle2 className="h-3.5 w-3.5" /> Accesible {keyInfo.agent_check_at && `(${formatDateTime(keyInfo.agent_check_at)})`}</span>
                  : keyInfo.agent_check_status ? <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-error)' }} title={keyInfo.agent_check_error ?? ''}><AlertCircle className="h-3.5 w-3.5" /> {keyInfo.agent_check_status}</span>
                  : 'Sin validar'}
              </Row>
              {keyInfo.agent_name && <Row label="Nombre / modelo del agente">{keyInfo.agent_name} · {keyInfo.agent_model}</Row>}
              {agentTools.length > 0 && <Row label="Herramientas del agente">{agentTools.map((t) => `${t.type}${t.mode ? ` (${t.mode})` : ''}${t.name ? `:${t.name}` : ''}`).join(', ')}</Row>}
              {keyInfo.agent_check_error && <p className="text-xs" style={{ color: 'var(--color-error)' }}>{keyInfo.agent_check_error}</p>}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>Sin API key configurada. La investigación IA está deshabilitada para este cliente.</p>
          )}

          <div className="space-y-2 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
            <label className="block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{keyInfo ? 'Rotar API key (opcional)' : 'Nueva API key'}</label>
            <input type="password" autoComplete="off" placeholder="sk-..." value={apiKey} onChange={(e) => setApiKey(e.target.value)} className={`${inputClass} font-mono`} style={inputStyle} />
            <label className="block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Modelo de la prueba de clave</label>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} placeholder={DEFAULT_MODEL} className={`${inputClass} font-mono`} style={inputStyle} />
            <label className="block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>ID del agente guardado (mismo proyecto OpenAI que la clave)</label>
            <input type="text" value={agentId} onChange={(e) => setAgentId(e.target.value)} placeholder="agent_..." className={`${inputClass} font-mono`} style={inputStyle} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Precio USD / 1M tokens entrada</label>
                <input type="number" min={0} step="0.01" value={priceIn} onChange={(e) => setPriceIn(e.target.value)} placeholder="desconocido" className={inputClass} style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Precio USD / 1M tokens salida</label>
                <input type="number" min={0} step="0.01" value={priceOut} onChange={(e) => setPriceOut(e.target.value)} placeholder="desconocido" className={inputClass} style={inputStyle} />
              </div>
            </div>
            <p className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>Sin precios el consumo se muestra en tokens y el coste como desconocido. Los agentes son recursos del proyecto de OpenAI: al rotar la clave hay que volver a validar el agente.</p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={handleSave} loading={saveMutation.isPending}><Save className="h-3.5 w-3.5" /> Guardar</Button>
              <Button variant="secondary" onClick={() => testKeyMutation.mutate()} loading={testKeyMutation.isPending} disabled={!keyInfo}><Key className="h-3.5 w-3.5" /> Probar clave</Button>
              <Button variant="secondary" onClick={() => validateAgentMutation.mutate()} loading={validateAgentMutation.isPending} disabled={!keyInfo?.agent_id}><Bot className="h-3.5 w-3.5" /> Validar agente</Button>
              {keyInfo && (
                <Button variant="secondary" onClick={() => { if (confirm(`Eliminar la API key de OpenAI de ${tenantName}? La investigación IA dejará de funcionar para este cliente.`)) deleteMutation.mutate() }} loading={deleteMutation.isPending}>
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Effective config + limits */}
        <div className="space-y-3 rounded-md border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Configuración efectiva de sesión</p>
          {keyInfo?.effective ? (
            <div className="space-y-1.5">
              <Row label="Versión de contexto">{keyInfo.effective.context_version}</Row>
              <Row label="Versión de instrucciones">{keyInfo.effective.prompt_version}</Row>
              <Row label="Entorno">{keyInfo.effective.environment}</Row>
              <Row label="Salida">{keyInfo.effective.output}</Row>
              <div>
                <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Herramientas por sesión (solo lectura)</p>
                <p className="text-xs" style={{ color: 'var(--color-text-primary)' }}>{keyInfo.effective.session_tools.join(', ')}</p>
              </div>
              <p className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>Las instrucciones de ROI365 se añaden a las del agente guardado en cada sesión; el agente de OpenAI no se modifica desde la aplicación.</p>
            </div>
          ) : <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>Configura una clave para ver la configuración efectiva.</p>}

          <p className="border-t pt-3 text-xs font-semibold uppercase tracking-wider" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-tertiary)' }}>Presupuesto del mes</p>
          {budget ? (
            <div className="space-y-1.5">
              <Row label="Tokens consumidos">{monthTokens.toLocaleString('es-ES')} / {budget.limits.monthly_token_budget.toLocaleString('es-ES')} ({monthPct}%)</Row>
              <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-gray-200)' }}>
                <div className="h-full rounded-full" style={{ width: `${monthPct}%`, backgroundColor: monthPct >= 90 ? 'var(--color-error)' : 'var(--color-brand-secondary)' }} />
              </div>
              <Row label="Investigaciones del mes">{budget.month?.tasks ?? 0}{budget.month?.skipped_budget ? ` (${budget.month.skipped_budget} sin presupuesto)` : ''}</Row>
              <Row label="Coste del mes">{budget.month?.cost_eur != null ? formatCurrency(budget.month.cost_eur) : 'desconocido (sin precios)'}</Row>
              {(budget.month?.chat_threads ?? 0) > 0 && <Row label="De ello, preguntas al investigador">{budget.month!.chat_threads} hilo(s) ({(budget.month!.chat_threads ?? 0) - (budget.month!.chat_general_threads ?? 0)} por hallazgo, {budget.month!.chat_general_threads ?? 0} generales) · {(budget.month!.chat_tokens ?? 0).toLocaleString('es-ES')} tokens{budget.month!.chat_cost_eur != null ? ` · ${formatCurrency(budget.month!.chat_cost_eur)}` : ''}</Row>}
              <Row label="Límites por ejecución">{budget.limits.max_investigations_per_run} investigaciones · {budget.limits.max_minutes_per_investigation} min · {budget.limits.max_tokens_per_run.toLocaleString('es-ES')} tokens · {budget.limits.max_tool_calls_per_investigation} llamadas a herramientas</Row>
              <p className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>Los límites se editan con el botón de configuración del tenant.</p>
            </div>
          ) : <div className="h-16 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />}
        </div>

        {/* Controls + usage */}
        <div className="space-y-3 rounded-md border p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Controles</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => testResearchMutation.mutate()} loading={testResearchMutation.isPending} disabled={!keyInfo?.agent_id || budget?.activeRun}>
              <FlaskConical className="h-3.5 w-3.5" /> Prueba de investigación (datos sintéticos)
            </Button>
            <Link to="/admin/ai/runs"><Button variant="secondary" size="sm"><ShieldCheck className="h-3.5 w-3.5" /> Ver ejecuciones</Button></Link>
          </div>
          <p className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>La prueba abre una sesión con una oportunidad ficticia (sin datos del tenant), ejercita herramientas y salida estructurada y no publica nada.</p>

          <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Uso</p>
            <select value={usageDays} onChange={(e) => setUsageDays(Number(e.target.value))} className="rounded border px-2 py-1 text-xs" style={inputStyle}>
              <option value={7}>Ultimos 7 dias</option><option value={30}>Ultimos 30 dias</option><option value={90}>Ultimos 90 dias</option>
            </select>
          </div>
          {usage ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Coste</p>
                  <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{usage.totals.runs_without_cost > 0 && usage.totals.total_cost_eur === 0 ? '—' : formatCurrency(usage.totals.total_cost_eur)}</p>
                  {usage.totals.runs_without_cost > 0 && <p className="text-[10px]" style={{ color: 'var(--color-warning-dark)' }}>{usage.totals.runs_without_cost} ejecución(es) sin precio</p>}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Tokens (in / out)</p>
                  <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{(usage.totals.total_tokens_input / 1000).toFixed(0)}K / {(usage.totals.total_tokens_output / 1000).toFixed(0)}K</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Ejecuciones</p>
                  <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{usage.totals.total_runs}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Fallidas</p>
                  <p className="text-lg font-bold tabular-nums" style={{ color: usage.totals.failed_runs > 0 ? 'var(--color-error)' : 'var(--color-text-primary)' }}>{usage.totals.failed_runs}</p>
                </div>
              </div>
              {usage.byDay.length > 0 ? (
                <div className="max-h-40 overflow-y-auto rounded border" style={{ borderColor: 'var(--color-border)' }}>
                  <table className="w-full text-xs">
                    <thead style={{ backgroundColor: 'var(--color-gray-50)' }}><tr><th className="px-2 py-1 text-left">Fecha</th><th className="px-2 py-1 text-right">Runs</th><th className="px-2 py-1 text-right">Tokens</th><th className="px-2 py-1 text-right">Coste</th></tr></thead>
                    <tbody>
                      {usage.byDay.map((d) => (
                        <tr key={d.date}><td className="px-2 py-1 tabular-nums">{d.date}</td><td className="px-2 py-1 text-right tabular-nums">{d.runs}</td><td className="px-2 py-1 text-right tabular-nums">{((d.tokens_input + d.tokens_output) / 1000).toFixed(1)}K</td><td className="px-2 py-1 text-right tabular-nums">{formatCurrency(d.cost_eur)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sin actividad en el periodo seleccionado.</p>}
            </>
          ) : <div className="h-24 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />}
        </div>
      </div>
    </div>
  )
}

const DEFAULT_FORM: AiTenantConfig = {
  feature_enabled: false, visibility_mode: 'approved_only', auto_publish: false, strategy: 'conservative', generation_frequency: 'on_sync',
  max_investigations_per_run: 12, max_minutes_per_investigation: 10, max_tokens_per_run: 2_000_000, monthly_token_budget: 20_000_000, max_tool_calls_per_investigation: 12,
}

export function AdminAiTenantsPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [panelTenantId, setPanelTenantId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<AiTenantConfig>(DEFAULT_FORM)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'tenants'] })

  const { data: response, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'ai', 'tenants', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), perPage: '20' })
      if (search) params.set('search', search)
      return apiFetchJson<{ ok: boolean; data: AiTenantRow[]; meta: { page: number; totalPages: number; total: number } }>(`/admin/ai/tenants?${params}`)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ tenantId, enabled }: { tenantId: string; enabled: boolean }) => api.put(`/admin/ai/tenants/${tenantId}/toggle`, { enabled }),
    onSuccess: () => { toast.success('Estado IA actualizado'); invalidate() },
    onError: (err) => toast.error((err as Error).message),
  })

  const configMutation = useMutation({
    mutationFn: ({ tenantId, config }: { tenantId: string; config: AiTenantConfig }) =>
      api.put(`/admin/ai/tenants/${tenantId}/config`, {
        featureEnabled: Boolean(config.feature_enabled),
        visibilityMode: config.visibility_mode,
        autoPublish: Boolean(config.auto_publish),
        strategy: config.strategy,
        generationFrequency: config.generation_frequency,
        maxInvestigationsPerRun: Number(config.max_investigations_per_run),
        maxMinutesPerInvestigation: Number(config.max_minutes_per_investigation),
        maxTokensPerRun: Number(config.max_tokens_per_run),
        monthlyTokenBudget: Number(config.monthly_token_budget),
        maxToolCallsPerInvestigation: Number(config.max_tool_calls_per_investigation),
      }),
    onSuccess: () => { toast.success('Configuracion actualizada'); invalidate(); queryClient.invalidateQueries({ queryKey: ['admin', 'ai', 'budget'] }); setEditingId(null) },
    onError: (err) => toast.error((err as Error).message),
  })

  const generateMutation = useMutation({
    mutationFn: (tenantId: string) => api.post(`/admin/ai/tenants/${tenantId}/generate`),
    onSuccess: () => { toast.success('Análisis encolado'); invalidate() },
    onError: (err) => toast.error((err as Error).message),
  })

  const startEdit = async (t: AiTenantRow) => {
    setEditingId(t.id)
    try {
      const cfg = await api.get<Partial<AiTenantConfig> & { feature_enabled: number | boolean; auto_publish: number | boolean }>(`/admin/ai/tenants/${t.id}/config`)
      setEditForm({
        feature_enabled: Boolean(cfg.feature_enabled),
        visibility_mode: cfg.visibility_mode ?? 'approved_only',
        auto_publish: Boolean(cfg.auto_publish),
        strategy: cfg.strategy ?? 'conservative',
        generation_frequency: cfg.generation_frequency ?? 'on_sync',
        max_investigations_per_run: cfg.max_investigations_per_run ?? DEFAULT_FORM.max_investigations_per_run,
        max_minutes_per_investigation: cfg.max_minutes_per_investigation ?? DEFAULT_FORM.max_minutes_per_investigation,
        max_tokens_per_run: cfg.max_tokens_per_run ?? DEFAULT_FORM.max_tokens_per_run,
        monthly_token_budget: cfg.monthly_token_budget ?? DEFAULT_FORM.monthly_token_budget,
        max_tool_calls_per_investigation: cfg.max_tool_calls_per_investigation ?? DEFAULT_FORM.max_tool_calls_per_investigation,
      })
    } catch (err) {
      toast.error((err as Error).message)
      setEditForm({ ...DEFAULT_FORM, feature_enabled: Boolean(t.ai_enabled), visibility_mode: t.visibility_mode, auto_publish: Boolean(t.auto_publish), strategy: t.strategy, generation_frequency: t.generation_frequency })
    }
  }

  const tenants = response?.data ?? []
  const th = (h: string) => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
  const td = (children: React.ReactNode, key?: string) => <td key={key} className="border-t px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>{children}</td>

  return (
    <div className="space-y-6">
      <PageHeader title="IA - Configuracion por Tenant" description="Investigador IA: activación, agente, límites y ejecución por cliente" />

      <div className="flex items-center gap-3">
        <div className="relative flex-1" style={{ maxWidth: '350px' }}>
          <Search className="absolute left-3 top-2.5 h-4 w-4" style={{ color: 'var(--color-text-tertiary)' }} />
          <input type="text" placeholder="Buscar tenant..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} className="w-full rounded-md border py-2 pl-9 pr-3 text-sm" style={inputStyle} />
        </div>
      </div>

      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: 'var(--color-gray-50)' }}>{['Tenant', 'Plan', 'IA', 'Agente', 'Estrategia', 'Visibilidad', 'Ultima ejecucion', 'Hallazgos', ''].map(th)}</tr></thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => <tr key={i}><td colSpan={9} className="px-4 py-4"><div className="h-10 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} /></td></tr>)
              ) : isError ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-error)' }}>Error cargando tenants. <button className="underline" onClick={() => refetch()}>Reintentar</button></td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center"><EmptyState icon={Settings} title="Sin tenants" description="No se encontraron tenants con los filtros actuales" /></td></tr>
              ) : (
                tenants.map((t) => (
                  <tr key={t.id}>
                    {td(<p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{t.name}</p>, 'n')}
                    {td(<StatusBadge status={t.plan} />, 'p')}
                    {td(
                      <button onClick={() => toggleMutation.mutate({ tenantId: t.id, enabled: !t.ai_enabled })} className="flex items-center gap-1" disabled={toggleMutation.isPending}>
                        {t.ai_enabled ? <ToggleRight className="h-6 w-6" style={{ color: 'var(--color-success)' }} /> : <ToggleLeft className="h-6 w-6" style={{ color: 'var(--color-text-tertiary)' }} />}
                      </button>, 'ia',
                    )}
                    {td(
                      !t.agent_configured ? <Badge variant="warning">Sin agente</Badge>
                        : t.agent_check_status === 'ok' ? <Badge variant="success">Validado</Badge>
                        : t.agent_check_status ? <Badge variant="error">{t.agent_check_status}</Badge>
                        : <Badge variant="neutral">Sin validar</Badge>, 'ag',
                    )}
                    {td(<span style={{ color: 'var(--color-text-secondary)' }}>{strategyLabels[t.strategy] ?? t.strategy}</span>, 's')}
                    {td(<span style={{ color: 'var(--color-text-secondary)' }}>{visibilityModeLabels[t.visibility_mode] ?? t.visibility_mode}</span>, 'v')}
                    {td(<span className="tabular-nums">{formatDateTime(t.last_run_at)}{t.active_runs > 0 && <Badge variant="info" className="ml-2">en curso</Badge>}</span>, 'l')}
                    {td(<span className="font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{t.recommendation_count}</span>, 'r')}
                    {td(
                      <div className="flex gap-1">
                        <Button variant="secondary" size="sm" onClick={() => startEdit(t)}><Settings className="h-3.5 w-3.5" /></Button>
                        <Button variant="secondary" size="sm" onClick={() => setPanelTenantId(panelTenantId === t.id ? null : t.id)}><Bot className="h-3.5 w-3.5" /> Investigador</Button>
                        <Button variant="primary" size="sm" onClick={() => generateMutation.mutate(t.id)} loading={generateMutation.isPending && generateMutation.variables === t.id} disabled={!t.ai_enabled || !t.agent_configured || t.active_runs > 0}><Play className="h-3.5 w-3.5" /> Analizar</Button>
                      </div>, 'a',
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {editingId && (
            <div className="border-t p-6" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
              <h4 className="mb-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Configuracion IA — {tenants.find((t) => t.id === editingId)?.name}</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <label className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  <input type="checkbox" checked={editForm.feature_enabled} onChange={(e) => setEditForm((f) => ({ ...f, feature_enabled: e.target.checked }))} className="rounded" /> IA habilitada para el tenant
                </label>
                <div>
                  <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Modo de visibilidad</label>
                  <select value={editForm.visibility_mode} onChange={(e) => setEditForm((f) => ({ ...f, visibility_mode: e.target.value }))} className={inputClass} style={inputStyle}>
                    <option value="all">Todas</option><option value="approved_only">Solo aprobadas</option><option value="none">Ninguna</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  <input type="checkbox" checked={editForm.auto_publish} onChange={(e) => setEditForm((f) => ({ ...f, auto_publish: e.target.checked }))} className="rounded" /> Auto-publicar hallazgos validados
                </label>
                <div>
                  <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Estrategia</label>
                  <select value={editForm.strategy} onChange={(e) => setEditForm((f) => ({ ...f, strategy: e.target.value }))} className={inputClass} style={inputStyle}>
                    <option value="automatic">Automatica</option><option value="conservative">Conservadora</option><option value="aggressive">Agresiva</option><option value="high_confidence_only">Solo alta confianza</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Generación programada</label>
                  <select value={editForm.generation_frequency} onChange={(e) => setEditForm((f) => ({ ...f, generation_frequency: e.target.value }))} className={inputClass} style={inputStyle}>
                    <option value="on_sync">Tras cada sincronización</option><option value="on_demand">Solo bajo demanda</option><option value="daily">Diaria (tras la sincronización nocturna)</option>
                  </select>
                </div>
                {([
                  ['max_investigations_per_run', 'Investigaciones por ejecución'],
                  ['max_minutes_per_investigation', 'Minutos máx. por investigación'],
                  ['max_tool_calls_per_investigation', 'Llamadas a herramientas por investigación'],
                  ['max_tokens_per_run', 'Tokens máx. por ejecución'],
                  ['monthly_token_budget', 'Presupuesto mensual de tokens'],
                ] as Array<[keyof AiTenantConfig, string]>).map(([k, label]) => (
                  <div key={k}>
                    <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{label}</label>
                    <input type="number" min={1} value={editForm[k] as number} onChange={(e) => setEditForm((f) => ({ ...f, [k]: Number(e.target.value) }))} className={inputClass} style={inputStyle} />
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button onClick={() => configMutation.mutate({ tenantId: editingId, config: editForm })} loading={configMutation.isPending}><Save className="h-4 w-4" /> Guardar</Button>
                <Button variant="secondary" onClick={() => setEditingId(null)}><X className="h-4 w-4" /> Cancelar</Button>
              </div>
            </div>
          )}

          {panelTenantId && <ResearchPanel tenantId={panelTenantId} tenantName={tenants.find((t) => t.id === panelTenantId)?.name ?? ''} onClose={() => setPanelTenantId(null)} />}

          {(response?.meta?.totalPages ?? 1) > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>{response?.meta?.total ?? 0} tenants</p>
              <div className="flex gap-1">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <span className="flex items-center px-3 text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{page} / {response?.meta?.totalPages ?? 1}</span>
                <Button variant="secondary" size="sm" disabled={page >= (response?.meta?.totalPages ?? 1)} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
